// 11 §2 — boot: load wasm, resolve the atlas manifest, start Phaser, build the SpacetimeDB
// connection, mount React under `SpacetimeDBProvider` (which connects), start the presence beat.
import { createRoot } from 'react-dom/client';
import { StrictMode, useMemo } from 'react';
import { SpacetimeDBProvider } from 'spacetimedb/react';
import { App, BOOT_PHASES } from './ui/App';
import { LoadingScreen, Toasts } from './ui/components';
import { startGame } from './game/Game';
import { resolveManifest } from './game/assets';
import { useSession } from './core/stores/session';
import { useLink } from './core/stores/link';
import { useSettings } from './core/stores/settings';
import { initWasm } from './core/sim/wasm';
import { WasmSimBridge } from './core/sim/WasmSimBridge';
import { installCatalogue } from './core/sim/actions';
import { installConsumables } from './core/sim/items';
import { installBridge } from './core/encounter/session';
import { connectionBuilder, getConn } from './core/net/connection';
import { heroLookNow } from './core/heroes/looks';
import { lookKey } from './game/heroKit';
import { startPresence } from './core/presence';
import { startAudio } from './core/audio';
import { startServerCues } from './core/audio/cues';
import { startEmotes } from './core/emote/store';
import { installUiSounds } from './ui/sfx';
import { UpdateBanner } from './ui/desktop/UpdateBanner';
import './ui/components/tokens.css';
import './ui/components/components.css';
import './ui/encounter/encounter.css';
import './ui/screens/screens.css';
import './ui/screens/world.css';
import './ui/screens/lobby.css';
import './ui/screens/chrome.css';
import './ui/screens/adventure.css';
import './ui/screens/spoils.css';
// static: the World roster and lobby already bundle it, so a dynamic import would not split it off
import { HeroForge } from './ui/screens/HeroForge';

/** The Provider over the current builder; docs/54 RC3 Reconnect now swaps the builder when the SDK
 * has given up, and the Provider's pool builds a new connection from it under the same hooks. */
function Connected({ rulesetVersion }: { rulesetVersion: number }) {
  const rebuilds = useLink((s) => s.rebuilds);
  const builder = useMemo(() => connectionBuilder({ rulesetVersion }), [rulesetVersion, rebuilds]);
  return <SpacetimeDBProvider connectionBuilder={builder}><App /><UpdateBanner /></SpacetimeDBProvider>;
}

async function boot(): Promise<void> {
  // docs/34: the audio engine first, so the interface's sounds are decoded by the time it shows
  startAudio();
  installUiSounds();
  const gameHost = document.getElementById('game')!;
  const rootHost = document.getElementById('root')!;
  const root = createRoot(rootHost);
  // `kit=<key>` also picks the tiling kit inside `?gallery=1`; only without it does `kit` mean the component library
  const kit = new URLSearchParams(window.location.search).get('gallery') ? null : new URLSearchParams(window.location.search).get('kit');
  const forge = new URLSearchParams(window.location.search).get('forge');
  // `?studio=1` (dev server only, `pnpm studio`): the Art Studio (docs/68, docs/70) — draw the custom
  // overworld tiles over a live sample chart and save them into the repo; no server, no Phaser.
  if (import.meta.env.DEV && new URLSearchParams(window.location.search).get('studio')) {
    const { ArtStudio } = await import('./ui/studio/ArtStudio');
    rootHost.style.position = 'static'; rootHost.style.pointerEvents = 'auto'; gameHost.style.display = 'none';
    root.render(<StrictMode><ArtStudio /></StrictMode>);
    return;
  }
  // `?sfx=1`: the sound board (docs/34) — every bundled bank, band and variant, playable; no server, no Phaser.
  if (new URLSearchParams(window.location.search).get('sfx')) {
    const { SoundBoard } = await import('./ui/kit/SoundBoard');
    root.render(<StrictMode><SoundBoard /></StrictMode>);
    return;
  }
  // `?upgrades=1`: the upgrade book (docs/112) — every enchantment and improvement, its ranks, a piece wearing it; no server, no sim.
  if (new URLSearchParams(window.location.search).get('upgrades')) {
    const { UpgradeBook } = await import('./ui/kit/UpgradeBook');
    root.render(<StrictMode><UpgradeBook /></StrictMode>);
    return;
  }
  // `?itemqa=1`: the item art QA sheet (game/itemArt.ts) — 24 px icons, upgraded, and worn on the 32 px body; no server, no sim.
  if (new URLSearchParams(window.location.search).get('itemqa')) {
    const { ItemArtQa } = await import('./ui/kit/ItemArtQa');
    root.render(<StrictMode><ItemArtQa /></StrictMode>);
    return;
  }
  // `?sets=1`: the set book (docs/110) — every item set, its cards in each state and its fanfare; no server, no sim.
  if (new URLSearchParams(window.location.search).get('sets')) {
    const { SetBook } = await import('./ui/kit/SetBook');
    root.render(<StrictMode><SetBook /></StrictMode>);
    return;
  }
  const story = ['intro', 'epilogue', 'writing', 'saga', 'chapter'].some((k) => new URLSearchParams(window.location.search).get(k));
  const opening = ['faceoff', 'escape', 'foedefeated'].some((k) => new URLSearchParams(window.location.search).get(k));
  const spells = ['spells', 'moves', 'statuses', 'items'].some((k) => new URLSearchParams(window.location.search).get(k));
  if (!kit && !forge && !story && !opening && !spells) root.render(<StrictMode><LoadingScreen phases={BOOT_PHASES} current="booting" /></StrictMode>);

  // 1. the simulator (11 §6.1); its ruleset_version feeds the compat gate
  const wasm = await initWasm();
  const bridge = new WasmSimBridge(wasm);
  installCatalogue(bridge.abilities());
  installConsumables(bridge.consumables());
  installBridge(bridge);
  console.info(`[fellowkeep] fk-sim-wasm ruleset ${bridge.rulesetVersion}`);

  // 2. art + Phaser
  const manifest = await resolveManifest();
  console.info(`[fellowkeep] atlas manifest: ${manifest.source}, CELL=${manifest.cell}`);
  // `?kit=1`: the component-library gallery (ui/kit/KitGallery.tsx); no server, no Phaser.
  if (kit) {
    const { KitGallery } = await import('./ui/kit/KitGallery');
    root.render(<StrictMode><KitGallery /></StrictMode>);
    return;
  }
  // `?forge=1`: HeroForge alone over the live wasm (docs/17 §4); no server, no Phaser. Forging
  // itself needs a connection, so the button only toasts the reducer error here.
  if (forge) {
    rootHost.style.position = 'static'; rootHost.style.pointerEvents = 'auto'; gameHost.style.position = 'fixed';
    root.render(
      <StrictMode>
        <main className="fk-hit" style={{ minHeight: '100vh', padding: 'var(--fk-space-5)', maxWidth: '84rem', margin: '0 auto' }} data-testid="forge-harness">
          {/* the forge reads a remade hero's origin through `useTable`, which needs a Provider even
              when it reads nothing (docs/82) */}
          <SpacetimeDBProvider connectionBuilder={connectionBuilder({ rulesetVersion: 0 })}>
            <HeroForge worldId={0n} compact={forge === 'compact'} fee={Number(new URLSearchParams(window.location.search).get('fee')) || 0} />
          </SpacetimeDBProvider>
        </main>
        <Toasts />
      </StrictMode>,
    );
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?intro=1` (`&epilogue=1`, `&writing=1`, `&saga=1`, or `?chapter=1` for docs/86): the docs/21 story screens over the
  // template story for `phrase`/`name`, with stand-in heroes; no server, no Phaser.
  if (story) {
    const { StoryHarness } = await import('./ui/screens/intro/Harness');
    const { MusicDirector } = await import('./core/audio/music');
    root.render(<StrictMode><MusicDirector /><StoryHarness /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?path=1` (docs/113): a quest's path over a library quest in any scene (`&scene=`, `&main=1`,
  // `&seed=`, `&season=`, `&at=`); no server.
  if (new URLSearchParams(window.location.search).get('path')) {
    const { PathHarness } = await import('./ui/screens/path/Harness');
    root.render(<StrictMode><PathHarness /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?chart=1` (docs/143): the World chart in the custom art over a sample realm (`&biome=`, `&season=`,
  // `&hour=` or `&night=1`, `&weather=`, `&seed=`, `&sites=`, `&sel=`, `&walk=`); no server, no Phaser.
  if (new URLSearchParams(window.location.search).get('chart')) {
    const { ChartHarness } = await import('./ui/screens/map/ChartHarness');
    root.render(<StrictMode><ChartHarness /><Toasts /></StrictMode>);
    return;
  }
  // `?village=1` (docs/51): the village view over a fixed village (`&spot=`, `&night=1`, `&season=`,
  // `&weather=`, `&biome=`, `&size=`, `&seed=`); no server, no Phaser.
  if (new URLSearchParams(window.location.search).get('village')) {
    const { VillageHarness } = await import('./ui/screens/village/Harness');
    const { MusicDirector } = await import('./core/audio/music');
    root.render(<StrictMode><MusicDirector /><VillageHarness /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?dice=1` (docs/116 CI4/CI5): the inn's dice table zoomed in over a local engine (`&mode=watch|seat`,
  // `&seats=1..3`, `&seed=`, `&state=open|playing|settling`); no server, no Phaser.
  if (new URLSearchParams(window.location.search).get('dice')) {
    const { DiceHarness } = await import('./ui/screens/village/inn/DiceHarness');
    root.render(<StrictMode><DiceHarness /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?faceoff=1` / `?escape=1` (docs/22 O4/B2): the opening's cinematics over the template story; `?foedefeated=1` the
  // "Foe Defeated!" card (docs/77); no server, no Phaser.
  if (opening) {
    const { OpeningHarness } = await import('./ui/encounter/OpeningHarness');
    const { MusicDirector } = await import('./core/audio/music');
    root.render(<StrictMode><MusicDirector /><OpeningHarness /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?moves=1`: the move book (docs/53) — every foe move of the vocabulary performed by the sim
  // on a field, through the real playback and the module's sound director; no server.
  if (new URLSearchParams(window.location.search).get('moves')) {
    const { MoveBook } = await import('./ui/kit/MoveBook');
    startGame(gameHost, { manifest });
    root.render(<StrictMode><MoveBook bridge={bridge} /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?spells=1`: the spellbook (docs/47) — every spell cast by the sim on a field, through the real
  // playback and the module's sound director (both in wasm); no server.
  // `?flank=1`: the flank book (docs/103) — a rogue's backstab planned, previewed and played on the
  // spellbook's field through the real projection, playback and sound director; no server.
  // `?matter=1`: the matter book (docs/114) — interactive ground: oil, water, ice, kegs, braziers and
  // boulders meeting spells and hands on the spellbook's field, played through the real rules,
  // playback and sound director; no server. Lists every row of the interaction table.
  if (new URLSearchParams(window.location.search).get('matter')) {
    const { MatterBook } = await import('./ui/kit/MatterBook');
    startGame(gameHost, { manifest });
    root.render(<StrictMode><MatterBook bridge={bridge} /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?snares=1`: the trap book (docs/140) — all hundred dungeon traps, each sprung and run on its demo
  // crawl by the sim in wasm and played through the real board and playback; no server.
  if (new URLSearchParams(window.location.search).get('snares')) {
    const { SnareBook } = await import('./ui/kit/SnareBook');
    startGame(gameHost, { manifest });
    root.render(<StrictMode><SnareBook bridge={bridge} /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  if (new URLSearchParams(window.location.search).get('flank')) {
    const { FlankBook } = await import('./ui/kit/FlankBook');
    startGame(gameHost, { manifest });
    root.render(<StrictMode><FlankBook bridge={bridge} /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  if (new URLSearchParams(window.location.search).get('spells')) {
    const { Spellbook } = await import('./ui/kit/Spellbook');
    startGame(gameHost, { manifest });
    root.render(<StrictMode><Spellbook bridge={bridge} /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?items=1`: the item book (docs/72 IT20) — every catalogue piece drawn, worded and played on the
  // spellbook's field through the real playback; no server.
  if (new URLSearchParams(window.location.search).get('items')) {
    const { ItemBook } = await import('./ui/kit/ItemBook');
    startGame(gameHost, { manifest });
    root.render(<StrictMode><ItemBook bridge={bridge} /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?statuses=1`: the status book (docs/69) — every status's look, badge and note on the spellbook's
  // field, landed through the real playback; no server.
  if (new URLSearchParams(window.location.search).get('statuses')) {
    const { StatusBook } = await import('./ui/kit/StatusBook');
    startGame(gameHost, { manifest });
    root.render(<StrictMode><StatusBook bridge={bridge} /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?puzzles=1`: the puzzle-room gallery (docs/55) — forged rooms on the real board, any seed, any
  // party size, partway through their proven solution; no server.
  if (new URLSearchParams(window.location.search).get('puzzles')) {
    const { PuzzleBook } = await import('./ui/kit/PuzzleBook');
    startGame(gameHost, { manifest });
    root.render(<StrictMode><PuzzleBook bridge={bridge} /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?crawl=1`: the dungeon crawl gallery (docs/60) — forged crawls on the real board, any seed, theme
  // and party size, fogged as the party sees it or in the clear; no server.
  if (new URLSearchParams(window.location.search).get('crawl')) {
    const { CrawlBook } = await import('./ui/kit/CrawlBook');
    startGame(gameHost, { manifest });
    root.render(<StrictMode><CrawlBook bridge={bridge} /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?secrets=1`: the hidden things gallery (docs/76) — a staged fight with every hidden thing
  // planted, fresh, found, or found and opened, on the real board; no server.
  if (new URLSearchParams(window.location.search).get('secrets')) {
    const { SecretsBook } = await import('./ui/kit/SecretsBook');
    startGame(gameHost, { manifest });
    root.render(<StrictMode><SecretsBook bridge={bridge} /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?explore=1`: the exploration gallery (docs/61) — forged explorations on the real board, any seed,
  // air and party size, fogged as the party sees it or in the clear; no server.
  if (new URLSearchParams(window.location.search).get('explore')) {
    const { ExploreBook } = await import('./ui/kit/ExploreBook');
    startGame(gameHost, { manifest });
    root.render(<StrictMode><ExploreBook bridge={bridge} /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?fight=1`: a staged fight (docs/66) on the real Encounter board, with a called reserve on its
  // way, an arrival, a raising and the HUD's lines for them; no server.
  if (new URLSearchParams(window.location.search).get('fight') && !new URLSearchParams(window.location.search).get('gallery')) {
    const { FightBook } = await import('./ui/kit/FightBook');
    startGame(gameHost, { manifest });
    root.render(<StrictMode><FightBook /><Toasts /></StrictMode>);
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  // `?gallery=1`: the view-tiling art-review gallery (game/scenes/Gallery.ts); no server, no React shell.
  if (new URLSearchParams(window.location.search).get('gallery')) {
    root.render(null);   // drop the loading screen: the gallery is canvas only
    startGame(gameHost, { manifest, gallery: true });
    (window as unknown as { __fk?: unknown }).__fk = { bridge };
    return;
  }
  startGame(gameHost, { manifest });
  useSettings.getState().hydrateFrom(null);

  // 3. sign in (anonymous unless VITE_AUTH_ISSUER) and connect; the Provider owns the socket
  root.render(<StrictMode><LoadingScreen phases={BOOT_PHASES} current="auth" /></StrictMode>);
  await useSession.getState().signIn();
  startPresence();
  startServerCues();
  startEmotes();
  root.render(<StrictMode><Connected rulesetVersion={bridge.rulesetVersion} /></StrictMode>);
  // docs/145: a hero's kit look key, for e2e (what the store would draw for that hero id)
  const heroLook = (id: string) => { const l = heroLookNow(id); return l ? lookKey(l) : undefined; };
  (window as unknown as { __fk?: unknown }).__fk = { bridge, useSession, useLink, getConn, heroLook };
}

void boot();
